import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import {
  Accounts,
  CompareAccount,
  CustomAccount,
  Portfolio,
  RebalanceAccount,
  Storage,
} from 'actions';
import { notWealthica } from 'utils/helpers';
import { Radio, Label, Popup, Icon } from 'semantic-ui-react';
import { colors } from 'utils/colors';
import { TOOLS_STOCK_EXPOSURE } from 'utils/features';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import {
  Button,
  Card,
  Segment,
  Loading,
  Feature,
  ButtonFA,
} from '../../components';
import { ReallocateAccount } from '../../actions';

@connect((state) => ({
  accounts: state.Accounts.accountList,
  isFetching: state.Accounts.isFetching,
  selectedAccountList: state.Accounts.selectedAccountList,
  partnerName: state.Storage['partner-name'],
  overview: state.Accounts.overview,
  exchangeRate: state.Storage['pwpa-exchange-rate'],
  user_region: state.Storage.user_region,
  ui_exposure_checked: state.Storage.ui_exposure_checked,
  french: state.Storage.language === 'fr',
  wealthica_plan: state.Storage.wealthica_plan,
}))
export default class AccountsList extends Component {
  state = {
    multipleSelect: !!this.props.selectedAccountList.length,
    invalidSubmission: false,
    reminderIsOpen: false,
  };

  componentWillMount() {
    const { accounts, dispatch, ui_exposure_checked } = this.props;

    dispatch(Portfolio.setEditing('', '', ''));
    dispatch(CompareAccount.clearResult());
    dispatch(CompareAccount.removeAll());
    dispatch(RebalanceAccount.removeAll());
    dispatch(CustomAccount.clearCustomAccount());
    if (!ui_exposure_checked) {
      this.setReminderVisibility(true);
    }
    if (!accounts.length) {
      dispatch(Accounts.fetchAll());
    }

    return false;
  }

  componentWillUnmount() {
    const { dispatch, history } = this.props;

    if (!history.location.pathname.includes('/accounts/')) {
      dispatch(Accounts.toggleMultipleMode());
    }
  }

  setReminderVisibility = (isOpen) => this.setState({ reminderIsOpen: isOpen });

  canCreateAccount = (match, accountList) => {
    const { id } = match.params;
    const { partnerName } = this.props;
    const customAccounts = accountList.filter(
      ({ provider }) => provider === 'wsc' || provider === 'uld'
    );

    // Only check accounts if there isn't a target account.
    // Otherwise, the second step to creating an account could be blocked.
    if (
      (!Number(id) && customAccounts.length >= 10) ||
      partnerName === 'nbdb_sandbox' ||
      partnerName === 'nbdb'
    ) {
      return false;
    }

    return true;
  };

  canUseStockExposure = (accountList) => accountList.length > 0;

  parseNum = (num) => {
    num = !num ? 0 : Math.round(num * 100) / 100;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  handleReminderClick = () => {
    const { dispatch } = this.props;
    dispatch(Storage.setItem('ui_exposure_checked', true));
    dispatch(Accounts.checkUiExposure());
    this.setState({ reminderIsOpen: false });
  };

  render() {
    const {
      accounts,
      isFetching,
      history,
      dispatch,
      selectedAccountList,
      match,
      partnerName,
      overview,
      exchangeRate,
      user_region,
      french,
      wealthica_plan,
    } = this.props;
    const { multipleSelect, invalidSubmission, reminderIsOpen } = this.state;

    return (
      <Segment style={{ height: '100%', overflow: 'auto' }}>
        <Loading active={isFetching} french={french} />

        <StyledHeader size="1.5rem" color="black">
          {french ? 'Mes comptes' : 'My Accounts'}
          <div>
            {french ? (
              <SubHeader long>
                Notre analyse vous montre les valeurs nutritives de votre ou vos
                portefeuilles <strong>actuels</strong>. Depuis un compte,
                sélectionnez le bouton Analyser en haut à droite. <br /> <br />
                <strong>Notes:</strong> Actuellement, nous disposons de données
                sur les actions/FNB canadiens et américains, les fonds communs
                de placement et les fonds distincts canadiens, ainsi que les
                fonds de CÉIÉ en CAD/USD. Les comptes en USD seront convertis en
                CAD lorsque vous regroupez des comptes libellés en devises
                différentes.
              </SubHeader>
            ) : !notWealthica(partnerName) ? (
              <SubHeader long>
                Our analysis shows you the nutrition facts of your{' '}
                <strong>current </strong>
                portfolio(s). In an account, click the Analyze button in the top
                right. <br /> <br />
                <strong>Notes:</strong> Currently, we have data for Canadian and
                US stocks/ETFs, Canadian mutual and segregated funds, and
                CAD/USD HISA funds.
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  content={
                    french
                      ? 'Les fonds de CÉIÉ en CAD/USD sont inclus dans l’analyse en utilisant le rendement historique d’un fonds de CÉIÉ en CAD/USD représentatif détenu par une banque.'
                      : 'CAD/USD HISA funds are included in the analysis using the historical rates of a representative bank-owned CAD/USD HISA fund.'
                  }
                />
                Any other securities such as{' '}
                <b>private funds, individual bonds, and derivatives</b> will be
                excluded from the analysis. USD accounts will be converted to
                CAD when you group accounts in different currencies.
              </SubHeader>
            ) : (
              <SubHeader long>
                Our analysis shows you the nutrition facts of your{' '}
                <strong>current </strong>
                portfolio(s). In an account, click the Analyze button in the top
                right. <br /> <br />
                <strong>Notes:</strong> Currently, we have data for Canadian and
                US stocks/ETFs, Canadian mutual and segregated funds, and
                CAD/USD HISA funds.
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  content="CAD/USD HISA funds are included in the analysis using the historical rates of a representative bank-owned CAD/USD HISA fund."
                />
                Any other securities such as{' '}
                <b>private funds, individual bonds, and derivatives</b> will be
                excluded from the analysis. USD accounts will be converted to
                CAD when you group accounts in different currencies.
              </SubHeader>
            )}
          </div>
        </StyledHeader>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: '2rem',
            right: '2rem',
            width: '15rem',
          }}
        >
          {!notWealthica(partnerName) ? (
            <AddMoreAccs>
              <Button
                className={cn('left', {
                  disabled: !this.canCreateAccount(match, accounts),
                })}
                color={colors.red}
                icon="list"
                style={{ width: '15rem' }}
                popup={{
                  position: 'left center',
                  content: french
                    ? 'Enregistre jusqu’à 10 comptes personnalisés'
                    : 'There is a custom account limit of 10.',
                  wide: true,
                }}
                enabledPopup={{
                  position: 'left center',
                  content: french
                    ? 'Enregistre jusqu’à 10 comptes personnalisés'
                    : 'Save up to 10 custom accounts',
                  wide: true,
                }}
              >
                {french ? 'Ajouter des comptes' : 'Add Accounts'}
              </Button>
              {this.canCreateAccount(match, accounts) && (
                <div
                  style={{
                    width: '15rem',
                    zIndex: 4,
                  }}
                >
                  {wealthica_plan !== 'jubilee' ? (
                    <Popup
                      position="left center"
                      content={
                        french
                          ? 'Pour les abonnés de Wealthscope Jubilee'
                          : 'For Wealthscope Jubilee Subscribers'
                      }
                      style={{ width: '13rem' }}
                      mouseEnterDelay={100}
                      trigger={
                        <div
                          style={{
                            width: '100%',
                            display: 'block',
                          }}
                        >
                          <StyledTopLink
                            as="div"
                            style={{
                              background: '#f5f5f5',
                              color: '#aaa',
                              borderColor: '#ddd',
                              cursor: 'default',
                              pointerEvents: 'auto',
                            }}
                            to="#"
                          >
                            {french ? 'Téléversement IA' : 'AI Upload'}
                          </StyledTopLink>
                        </div>
                      }
                    />
                  ) : (
                    <StyledTopLink to="/accounts/upload">
                      {french ? 'Téléversement IA' : 'AI Upload'}
                    </StyledTopLink>
                  )}
                  <StyledLink to="/accounts/custom/new">
                    {french ? 'Saisie manuelle' : 'Manually Enter'}
                  </StyledLink>
                </div>
              )}
            </AddMoreAccs>
          ) : (
            <Button
              className={cn('left', {
                disabled: !this.canCreateAccount(match, accounts),
              })}
              icon="line chart"
              onClick={() => history.push('/accounts/custom/new')}
              color={colors.red}
              style={{
                width: '15rem',
                marginBottom: '1rem',
              }}
              popup={{
                position: 'left center',
                content: french
                  ? 'Enregistre jusqu’à 10 comptes personnalisés'
                  : 'There is a custom account limit of 10.',
                wide: true,
              }}
              enabledPopup={{
                position: 'left center',
                content: french
                  ? 'Enregistre jusqu’à 10 comptes personnalisés'
                  : 'Save up to 10 custom accounts',
                wide: true,
              }}
            >
              {french ? 'Saisie manuelle' : 'Manually Enter'}
            </Button>
          )}
        </div>

        <div>
          <Paragraph
            style={{ paddingRight: '0.9rem', paddingTop: '0.8rem' }}
            disabled={accounts.length < 2}
          >
            {french
              ? 'Regrouper les comptes aux fins de l’analyse?'
              : 'Group accounts for analysis?'}
          </Paragraph>
          <ToggleWrapper
            onClick={() => {
              if (accounts.length > 1) {
                this.setState({
                  multipleSelect: !multipleSelect,
                  invalidSubmission: false,
                });
                dispatch(Accounts.toggleMultipleMode());
              }
            }}
            groupDisabled={accounts.length < 2}
          >
            {accounts.length < 2 ? (
              <Popup
                hoverable
                trigger={
                  <CustomRadio
                    toggle
                    checked={multipleSelect}
                    onClick={() => {
                      dispatch(Accounts.saveSingleAccInfo(''));
                    }}
                    disabled
                  />
                }
                position="top center"
                content={
                  french
                    ? 'Nous pouvez utiliser cette fonction lorsque vous avez 2 comptes ou plus.'
                    : 'You can use this function when you have 2 or more accounts.'
                }
              />
            ) : (
              <CustomRadio
                toggle
                checked={multipleSelect}
                onClick={() => {
                  dispatch(Accounts.saveSingleAccInfo(''));
                }}
              />
            )}
          </ToggleWrapper>
          <Button
            className="left"
            icon="book"
            color={colors.blue}
            onClick={() => {
              if (selectedAccountList.length >= 2) {
                return history.push('/accounts/multiple');
              }
              return this.setState({ invalidSubmission: true });
            }}
            style={{
              display: `${multipleSelect ? 'inline-block' : 'none'}`,
              transform: 'translate(15px, 3px)',
            }}
          >
            {french ? 'Voir groupe' : 'View Group'}
          </Button>
          <Label
            basic
            color="red"
            pointing="left"
            content={
              french
                ? 'Veuillez sélectionner au moins deux comptes.'
                : 'Please select at least two accounts.'
            }
            style={{
              display: `${invalidSubmission ? 'inline-block' : 'none'}`,
              transform: 'translate(18px, -3px)',
            }}
          />
        </div>
        {!accounts.length ? null : (
          <Card
            to="/accounts/overview"
            color="red"
            hoverside="orange"
            header={french ? "Vue d'ensemble" : 'Overview'}
            content={
              french ? "Vue d'ensemble des comptes" : 'Accounts overview'
            }
            currency={user_region === 'US' ? 'USD' : 'CAD'}
            style={{
              opacity: `${multipleSelect ? '0.4' : '1'}`,
              pointerEvents: `${multipleSelect ? 'none' : 'auto'}`,
            }}
            onClick={() => {
              dispatch(Accounts.saveSingleAccInfo('overview'));
            }}
            balance={this.parseNum(overview.balance)}
          />
        )}
        {!accounts.length ? (
          <div>
            {french
              ? "Vous n'avez aucun compte enregistré."
              : "You don't have any saved accounts"}
          </div>
        ) : (
          accounts.map((acc) => (
            <Card
              to={multipleSelect ? '/accounts/user' : `/accounts/${acc.id}`}
              accoundId={acc.id}
              selectable={multipleSelect}
              selected={selectedAccountList.includes(acc)}
              key={acc.id}
              icon="chart line"
              color="red"
              hoverside="orange"
              header={acc.user_institution}
              content={`${acc.name}:${acc.label}`}
              warning={acc.rebal_alert || acc.rebal_update}
              rebal={acc.rebal_set}
              balance={this.parseNum(acc.balance)}
              currency={user_region === 'US' ? 'USD' : acc.currency}
              linked={
                acc.provider !== 'wsc' &&
                acc.provider !== 'uld' &&
                !notWealthica(partnerName)
              }
              style={{
                paddingRight: '30px',
              }}
              isActive={() => {
                if (multipleSelect) {
                  return selectedAccountList.includes(acc);
                }

                return false;
              }}
              onClick={() => {
                dispatch(
                  Accounts.saveSingleAccInfo(
                    acc.id.toString(),
                    acc.user_institution,
                    acc.label
                  )
                );

                if (multipleSelect) {
                  if (selectedAccountList.includes(acc)) {
                    return dispatch(
                      Accounts.deselectMultipleAccount(
                        acc,
                        selectedAccountList,
                        user_region,
                        exchangeRate
                      )
                    );
                  }

                  this.setState({ invalidSubmission: false });
                  return dispatch(
                    Accounts.selectMultipleAccount(
                      acc,
                      selectedAccountList,
                      user_region,
                      exchangeRate
                    )
                  );
                }

                return false;
              }}
            />
          ))
        )}
      </Segment>
    );
  }
}

AccountsList.propTypes = {
  accounts: PropTypes.array,
  dispatch: PropTypes.func,
  isFetching: PropTypes.bool,
  history: PropTypes.object,
  selectedAccountList: PropTypes.array,
  match: PropTypes.object.isRequired,
  partnerName: PropTypes.string,
  overview: PropTypes.object,
  exchangeRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user_region: PropTypes.string,
  ui_exposure_checked: PropTypes.bool,
  french: PropTypes.bool,
  wealthica_plan: PropTypes.string,
};

AccountsList.defaultProps = {
  accounts: [],
  dispatch: () => false,
  isFetching: false,
  history: {},
  selectedAccountList: [],
  partnerName: '',
  overview: {},
  exchangeRate: 1,
  user_region: 'CA',
  ui_exposure_checked: false,
  french: false,
  wealthica_plan: '',
};

const Paragraph = styled.p`
  color: black;
  opacity: 0.9;
  font-size: 1.14rem;
  max-width: 16rem;
  font-weight: 600;
  display: inline-block;
`;

const ToggleWrapper = styled.div`
  display: inline-block;
  transform: translateY(5px);

  .ui.toggle.checkbox .box:hover:before,
  .ui.toggle.checkbox label:hover:before {
    background: ${(props) =>
      props.groupDisabled ? 'rgba(0, 0, 0, 0.25) !important' : ''};
  }

  .ui.toggle.checkbox input:focus:checked~.box:before,
  .ui.toggle.checkbox input:focus:checked~label:before {
    background-color: ${colors.blue} !important;
  }

  .ui.toggle.checkbox input:checked ~ .box:before,
  .ui.toggle.checkbox input:checked ~ label:before {
    background-color: ${colors.blue} !important;
`;

const CustomRadio = styled(({ className, children, ...rest }) => (
  <Radio className={className} {...rest}>
    {children}
  </Radio>
))`
  label:before {
    background: rgba(0, 0, 0, 0.25) !important;
  }

  label:hover:before {
    background: rgba(0, 0, 0, 0.15) !important;
  }import { isQuoteMedia } from '../../utils/helpers';

`;

const StyledHeader = styled.div`
  margin: 0 0 2rem 0;
  color: ${(props) => props.color || 'inherit'};
  font-size: ${(props) => props.size || '1rem'};
`;

const SubHeader = styled.div`
  color: rgb(110, 110, 110);
  margin-top: 8px;
  font-size: 1.14rem;
  max-width: ${(props) => (props.long ? '47rem' : '39rem')};
`;

const AddMoreAccs = styled.div`
  width: 15rem;
  height: 36px;
  margin-bottom: 1rem;
  overflow: visible;
  position: relative;

  &:hover {
    height: 36px; /* keep the container height */
    z-index: 1000;
  }

  & > div {
    position: absolute;
    top: 36px;
    left: 0;
    width: 15rem;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    border-radius: 0 0 4px 4px;
    display: none;
    z-index: 1001;
  }

  &:hover > div {
    display: block;
  }
`;

const StyledLink = styled(Link)`
  display: block;
  background: white;
  height: 40px;
  width: 15rem;
  border: 1px solid lightgrey;
  z-index: 999;
  text-align: center;
  line-height: 40px;
  color: rgb(55, 55, 55);
  font-size: 0.9rem;
  cursor: pointer;
  border-top: 0;

  &:hover {
    background: ${colors.red};
    color: white;
    border-color: ${colors.red};
  }
`;

const StyledTopLink = styled(Link)`
  display: block;
  background: white;
  height: 40px;
  width: 15rem;
  border: 1px solid lightgrey;
  z-index: 999;
  text-align: center;
  line-height: 40px;
  color: rgb(55, 55, 55);
  font-size: 0.9rem;
  cursor: pointer;
  border-bottom: 1px solid lightgrey;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  &:hover {
    background: ${colors.red};
    color: white;
    border-color: ${colors.red};
  }
`;
